import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseSiteService, OccEndpointsService } from '@spartacus/core';
import { firstValueFrom } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private static readonly loadPromises = new Map<string, Promise<void>>();

  constructor(
    private readonly http: HttpClient,
    private readonly occEndpoints: OccEndpointsService,
    private readonly baseSiteService: BaseSiteService
  ) {}

  load(): Promise<void> {
    const cacheKey = 'googleMapsApiKey';
    const existing = GoogleMapsLoaderService.loadPromises.get(cacheKey);
    if (existing) {
      return existing;
    }

    const promise = this.resolveValueFromOcc()
      .then((apiKey) => this.injectScript(apiKey))
      .catch((err) => {
        GoogleMapsLoaderService.loadPromises.delete(cacheKey);
        throw err;
      });

    GoogleMapsLoaderService.loadPromises.set(cacheKey, promise);
    return promise;
  }

  private configUrl(baseSiteId: string, path: string): string {
    const baseUrl = this.occEndpoints.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false,
    });
    return `${baseUrl}/sanofiusocc/v2/${baseSiteId}/${path}`;
  }

  private resolveValueFromOcc(): Promise<string> {
    return firstValueFrom(
      this.baseSiteService.getActive().pipe(
        take(1),
        switchMap((baseSiteId) =>
          this.http.get<string>(
            this.configUrl(baseSiteId, 'config/google-maps-api-key')
          )
        ),
        map((apiKey) => (apiKey ?? '').trim())
      )
    );
  }

  private injectScript(apiKey: string): Promise<void> {
    if (!apiKey) {
      return Promise.reject(
        new Error(
          'Google Maps API key is not configured. Set googleMapsApiKey in Hybris local.properties.'
        )
      );
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (
          typeof google === 'undefined' ||
          typeof google.maps === 'undefined'
        ) {
          reject(new Error('Google Maps API failed to load.'));
        } else {
          resolve();
        }
      };
      script.onerror = () =>
        reject(new Error('Google Maps could not be loaded.'));
      document.head.appendChild(script);
    });
  }
}
